import { internalLogger } from '../../interface/v1/clientOsLogger';
import { decodeJWTPayload } from './JWTUtils';

/**
 * Checks if a token is expired or about to expire
 * @param token The token to check
 * @param thresholdMinutes Minutes before expiration to consider a token as expiring soon
 * @returns True if token is expired or about to expire, false otherwise
 */
export const isTokenExpiredOrExpiringSoon = (
  token: string,
  thresholdMinutes: number = 5
): boolean => {
  if (!token) return true;

  try {
    const decodedToken = decodeJWTPayload(token);

    // Check if token has expiration time
    if (!decodedToken.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const expirationTime = decodedToken.exp; // Token expiration time in seconds
    const tokenExpired = currentTime >= expirationTime;
    // Token is expired if current time is greater than expiration time
    if (tokenExpired) {
      /* istanbul ignore next */
      internalLogger?.debug?.d1?.({
        className: 'isTokenExpiredOrExpiringSoon',
        methodName: 'isTokenExpiredOrExpiringSoon()',
        variableName: 'tokenExpired',
        currentValue: tokenExpired
      });
      return true;
    }

    // Token is about to expire if it's within the threshold window
    const thresholdSeconds = thresholdMinutes * 60;
    const willExpireSoon = expirationTime - currentTime < thresholdSeconds;

    if (willExpireSoon) {
      /* istanbul ignore next */
      internalLogger?.debug?.d1?.({
        className: 'isTokenExpiredOrExpiringSoon',
        methodName: 'isTokenExpiredOrExpiringSoon()',
        variableName: 'willExpireSoon',
        currentValue: willExpireSoon
      });
    }

    return willExpireSoon;
  } catch (error) {
    internalLogger?.error('Error checking token expiration', error);
    return true; // If there's an error parsing the token, refresh to be safe
  }
};
